import React from "react";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Theme,
  TableSortLabel,
  Box,
  Chip,
  Link,
  Modal,
  IconButton,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { SortType } from "../../../../../models/SortType";
import { CustomMenu, CustomMenuItem } from "../../../../../styles/Shipmentstyles";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import { observer } from "mobx-react";
import { UrgentSourcingRaisedRequestDetails } from "../../../../../models/UrgentSourcing/UrgentSourcing";
import { formatDate } from "../../../../../views/components/common/functions/formatDate";
import CloseIcon from "@mui/icons-material/Close";
import { useStores } from "../../../use-stores";

interface UrgentSourcingRequestTableProps {
  data: UrgentSourcingRaisedRequestDetails[];
  maxHeight?: number;
  minHeight?: number;
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy?: string;
  sortType?: SortType;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onSortChange?: (column: string, sortType: SortType) => void;
  onDone?: (id: number) => void;
  onCancel?: (id: number) => void;
}

const UrgentSourcingRequestTable = ({
  data,
  maxHeight,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  minHeight = window.innerHeight - 350,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
  onDone,
  onCancel,
}: UrgentSourcingRequestTableProps) => {
  const theme = useTheme<Theme>();
  const { urgentSourcingStore } = useStores();

  const isOpen = Boolean(urgentSourcingStore.anchorEl);

  const handleRowsChange = (value: number) => {
    onPageChange(null, 1);
    onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  return (
    <>
      {data.length > 0 ? (
        <Box>
          <TableContainer sx={{ maxHeight: maxHeight, minHeight: minHeight }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <AppTableHeaderCell
                    sx={{
                      textAlign: "center",
                      fontSize: 14,
                      position: "sticky",
                      left: 0,
                      zIndex: 20,
                      minWidth: "60px",
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "id"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange?.(
                          "id",
                          sortBy === "id" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => <span>{renderSortIcon("id")}</span>}
                      sx={{ fontSize: "12px" }}
                    >
                      ID
                    </TableSortLabel>
                  </AppTableHeaderCell>
                  <AppTableHeaderCell
                    minWidth="200px"
                    sx={{
                      position: "sticky",
                      left: { md: 60 },
                      zIndex: { md: 20 },
                      textAlign: "center",
                      boxShadow: {
                        md: "10px 0 20px -12px rgba(115,115,115,0.3);",
                      },
                      backgroundColor: { md: theme.palette.background.paper },
                    }}
                  >
                    <TableSortLabel
                      active={sortBy === "product_name"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange?.(
                          "product_name",
                          sortBy === "product_name" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("product_name")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Product Name
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Lead ID
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Quantity
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    <TableSortLabel
                      active={sortBy === "timeline"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange?.(
                          "timeline",
                          sortBy === "timeline" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("timeline")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Timeline
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="130px">
                    Price Range
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    <TableSortLabel
                      active={sortBy === "priority"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange?.(
                          "priority",
                          sortBy === "priority" && sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("priority")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Priority
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="150px">Link</AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="150px">
                    Description
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="150px">
                    Cancel Reason
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Status
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="120px">
                    Catalog Creator
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="120px">
                    Catalog Created On
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Catalog Creation TAT
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="120px">
                    Product Creator
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="120px">
                    Product Created On
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    Product Creation TAT
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="120px">
                    Raised By
                  </AppTableHeaderCell>

                  <AppTableHeaderCell minWidth="100px">
                    <TableSortLabel
                      active={sortBy === "created_date"}
                      direction={sortType === SortType.ASC ? "asc" : "desc"}
                      onClick={() =>
                        onSortChange?.(
                          "created_date",
                          sortBy === "created_date" &&
                            sortType === SortType.DESC
                            ? SortType.ASC
                            : SortType.DESC
                        )
                      }
                      IconComponent={() => (
                        <span>{renderSortIcon("created_date")}</span>
                      )}
                      sx={{ fontSize: "12px" }}
                    >
                      Created Date
                    </TableSortLabel>
                  </AppTableHeaderCell>

                  <AppTableHeaderCell
                    sx={{
                      position: "sticky",
                      right: 0,
                      zIndex: 20,
                      boxShadow: "-10px 0 20px -12px rgba(115,115,115,0.3);",
                      backgroundColor: theme.palette.background.paper,
                      minWidth: "100px",
                    }}
                  >
                    Action
                  </AppTableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.map(
                  (item: UrgentSourcingRaisedRequestDetails, index) => (
                    <TableRow key={item.id}>
                      <AppTableRowCell
                        sx={{
                          fontSize: "12px",
                          textAlign: "center",
                          left: 0,
                          position: "sticky",
                          zIndex: 15,
                          boxShadow: "10px 0 20px -12px rgba(115,115,115,0.3);",
                          backgroundColor: theme.palette.background.paper,
                          fontWeight: 600,
                          minWidth: "60px",
                        }}
                      >
                        {item.id}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{
                          minWidth: "250px",
                          maxWidth: "300px",
                          fontSize: "12px",
                          textAlign: "center",
                          left: { md: 60 },
                          position: { xs: "static", md: "sticky" },
                          zIndex: { md: 15 },
                          boxShadow: {
                            md: "10px 0 20px -12px rgba(115,115,115,0.3)",
                          },
                          backgroundColor: {
                            md: theme.palette.background.paper,
                          },
                          fontWeight: 600,
                          whiteSpace: "normal",
                          wordWrap: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {item.product_name || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.lead_id || "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.quantity || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.timeline || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.min_price_range && item.max_price_range
                          ? `${item.min_price_range} - ${item.max_price_range}`
                          : "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        <Chip
                          label={
                            item.priority === 0
                              ? "High"
                              : item.priority === 1
                              ? "Medium"
                              : "Low"
                          }
                          size="small"
                          color={
                            item.priority === 0
                              ? "error"
                              : item.priority === 1
                              ? "warning"
                              : "success"
                          }
                          variant="outlined"
                        />
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.link ? (
                          <Link
                            href={item.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textDecoration: "underline",
                              fontSize: "12px",
                              color: theme.palette.blue.main,
                              fontWeight: 600,
                            }}
                          >
                            View
                          </Link>
                        ) : (
                          "-"
                        )}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.description ? (
                          <AppButton
                            label="View Desc"
                            variant="transparentPrimary"
                            customStyles={{
                              fontSize: "12px",
                              padding: "4px 8px",
                              minWidth: "auto",
                              color: theme.palette.blue.main,
                            }}
                            onClick={() => {
                              urgentSourcingStore.managePopup(
                                "openDescriptionModal",
                                "open",
                                {
                                  description: item.description || "",
                                  descriptionType: "product",
                                }
                              );
                            }}
                          />
                        ) : (
                          "-"
                        )}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.cancel_reason ? (
                          <AppButton
                            label="View Reason"
                            variant="transparentPrimary"
                            customStyles={{
                              fontSize: "12px",
                              padding: "4px 8px",
                              minWidth: "auto",
                              color: theme.palette.blue.main,
                            }}
                            onClick={() => {
                              urgentSourcingStore.managePopup(
                                "openDescriptionModal",
                                "open",
                                {
                                  description: item.cancel_reason || "",
                                  descriptionType: "cancel",
                                }
                              );
                            }}
                          />
                        ) : (
                          "-"
                        )}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        <Chip
                          label={
                            item.status === 0
                              ? "Active"
                              : item.status === 1
                              ? "Closed"
                              : "Cancelled"
                          }
                          size="small"
                          color={
                            item.status === 0
                              ? "success"
                              : item.status === 1
                              ? "info"
                              : "error"
                          }
                          variant="outlined"
                        />
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.product_catalog_creator_name || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {formatDate(item.product_catalog_created_date) || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.catalog_creation_tat
                          ? `${Math.floor(Number(item.catalog_creation_tat) / 60)} hr ${
                              Number(item.catalog_creation_tat) % 60
                            } min`
                          : "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.product_creator_name || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {formatDate(item.product_created_date) || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.product_creation_tat
                          ? `${Math.floor(Number(item.product_creation_tat) / 60)} hr ${
                              Number(item.product_creation_tat) % 60
                            } min`
                          : "-"}
                      </AppTableRowCell>

                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {item.requestor_name || "-"}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{ textAlign: "center", fontSize: "12px" }}
                      >
                        {formatDate(item.created_date)}
                      </AppTableRowCell>
                      <AppTableRowCell
                        sx={{
                          textAlign: "center",
                          fontSize: "12px",
                          position: "sticky",
                          right: 0,
                          zIndex: 15,
                          boxShadow:
                            "-10px 0 20px -12px rgba(115,115,115,0.3);",
                          backgroundColor: theme.palette.background.paper,
                        }}
                      >
                        {item.status === 0 ? (
                          <>
                            <IconButton
                              onClick={(event) => {
                                urgentSourcingStore.setAnchorEl(
                                  event.currentTarget,
                                  item.id
                                );
                              }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                            <CustomMenu
                              anchorEl={urgentSourcingStore.anchorEl}
                              onClose={() =>
                                urgentSourcingStore.setAnchorEl(null)
                              }
                              open={isOpen}
                              anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                              }}
                              transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                              }}
                            >
                             <CustomMenuItem
                                onClick={() => {
                                  if (urgentSourcingStore.selectedRequestId) {
                                    onDone?.(urgentSourcingStore.selectedRequestId);
                                  }
                                  urgentSourcingStore.setAnchorEl(null);
                                }}
                              >
                                Approve
                              </CustomMenuItem>
                              <CustomMenuItem
                                onClick={() => {
                                  if (urgentSourcingStore.selectedRequestId) {
                                    onCancel?.(urgentSourcingStore.selectedRequestId);
                                  }
                                  urgentSourcingStore.setAnchorEl(null);
                                }}
                              >
                                Reject
                              </CustomMenuItem>
                            </CustomMenu>
                          </>
                        ) : (
                          <Chip
                            label={
                              item.status === 1 ? "Completed" : "Cancelled"
                            }
                            size="small"
                            color={item.status === 1 ? "success" : "error"}
                            variant="outlined"
                          />
                        )}
                      </AppTableRowCell>
                    </TableRow>
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <AppTablePagenation
            fontSize="12px"
            pageNo={page_no}
            limit={limit}
            totalCount={total_count}
            onPageChange={onPageChange}
            handleRowsChange={handleRowsChange}
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "200px",
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body1">No data available</Typography>
        </Box>
      )}
      <Modal
        open={urgentSourcingStore.popupState.openDescriptionModal}
        onClose={() =>
          urgentSourcingStore.managePopup("openDescriptionModal", "close")
        }
        aria-labelledby="description-modal-title"
        aria-describedby="description-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 800,
            maxHeight: "80vh",
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: 24,
            p: 3,
            overflow: "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <CustomTypography variant="heading5" fontWeight={600}>
              {urgentSourcingStore.selectedDescriptionType === "product"
                ? "Product Description"
                : urgentSourcingStore.selectedDescriptionType === "cancel"
                ? "Cancel Reason Description"
                : "Description"}
            </CustomTypography>
            <IconButton
              onClick={() =>
                urgentSourcingStore.managePopup("openDescriptionModal", "close")
              }
              sx={{
                color: theme.palette.grey[500],
                "&:hover": {
                  color: theme.palette.grey[700],
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              backgroundColor: theme.palette.grey[50],
              p: 2,
              borderRadius: 1,
              border: `1px solid ${theme.palette.grey[200]}`,
              maxHeight: "60vh",
              overflow: "auto",
            }}
          >
            <Box
              dangerouslySetInnerHTML={{
                __html:
                  urgentSourcingStore.selectedDescription ||
                  "No description available",
              }}
              sx={{
                color: theme.palette.text.secondary,
                fontSize: "14px",
                lineHeight: "1.6",
                "& h1, & h2, & h3, & h4, & h5, & h6": {
                  marginTop: "16px",
                  marginBottom: "8px",
                },
                "& p": {
                  marginBottom: "12px",
                },
                "& ul, & ol": {
                  marginBottom: "12px",
                  paddingLeft: "20px",
                },
                "& li": {
                  marginBottom: "4px",
                },
              }}
            />
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default observer(UrgentSourcingRequestTable);
